import {} from './locales.js';
/**
 * Dates and times for display. `LOC-004` `MUST`: "Locale-correct number, currency, date and
 * address formats. Decimal separator errors in a product where 0.25 dioptre steps matter are a
 * safety issue, not a cosmetic one."
 *
 * **Locale-correct here means the words are the locale's and the digits are not.** `D-03` and
 * `TY-03` fix Western digits everywhere including the Urdu interface, so the month name comes from
 * the locale (`7 Oct 2026`, or the Urdu month name) and every number is a Western digit. `ur-IN`
 * and `ar-EG` both default to Arabic-Indic digits in ICU, which is why the tag is built here with
 * `nu-latn` stated rather than taken from whichever locale a caller holds.
 *
 * `formatToParts`, not `format`, for the reason `packages/domain/src/branch-date.ts` gives: the
 * numbers are read out of the parts and reassembled, so separators and field order are this
 * product's and not the runtime ICU's.
 *
 * Money is not here. `apps/counter/src/money.ts` owns the minor-unit exponent and the grouping, and
 * `Intl` reports PKR as a zero-decimal currency, which is how an export once wrote every rupee a
 * hundred times too large. The exponent belongs to this product, never to a locale library.
 */
export class DisplayFormatError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DisplayFormatError';
    }
}
function partsOf(instant, timeZone, locale, options) {
    if (!Number.isFinite(instant))
        throw new DisplayFormatError(`Not an instant: ${String(instant)}`);
    if (timeZone === '') {
        throw new DisplayFormatError('A display time needs the branch timezone (BR-01), not the browser.');
    }
    try {
        return new Intl.DateTimeFormat(`${locale}-u-ca-gregory-nu-latn`, {
            timeZone,
            calendar: 'gregory',
            numberingSystem: 'latn',
            ...options,
        }).formatToParts(new Date(instant));
    }
    catch {
        throw new DisplayFormatError(`${timeZone} is not a timezone this runtime knows.`);
    }
}
const part = (parts, type) => {
    const found = parts.find((candidate) => candidate.type === type);
    if (found === undefined)
        throw new DisplayFormatError(`No ${type} in the formatted date.`);
    return found.value;
};
/**
 * `7 Oct 2026`: day, month name in the locale, year, in the branch's own timezone (Appendix J).
 * The order is day-month-year in both locales, which is how Pakistan writes it.
 */
export function formatDisplayDate(instant, timeZone, locale) {
    const parts = partsOf(instant, timeZone, locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    return `${String(Number(part(parts, 'day')))} ${part(parts, 'month')} ${part(parts, 'year')}`;
}
/** `14:05`, 24-hour, in the branch's timezone. */
export function formatDisplayTime(instant, timeZone, locale) {
    const parts = partsOf(instant, timeZone, locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    return `${part(parts, 'hour').padStart(2, '0')}:${part(parts, 'minute').padStart(2, '0')}`;
}
/**
 * One instant, as the day and the time the branch was having. `LOC-004`, Appendix J.
 *
 * `formatDay` below answers "which day", which is what almost every screen needs. A few need the
 * time as well, and "when the money came in" is the clearest: a cash count reconciles against it.
 *
 * **The branch's zone, never the device's.** `OrderDetail` had its own `formatTakenAt` that read
 * `date.getHours()` off the browser, with a comment explaining it was "this device's local time"
 * as though that settled it. A counter tablet set to the wrong zone, or a branch that is not in
 * the zone the device thinks it is, then reported a payment at the wrong hour and sometimes on the
 * wrong day. The same function also rendered `2026-10-05`, which is the wire's format and not the
 * one a shop writes.
 *
 * Returns the value unchanged when it cannot be parsed, like `formatDay`: a screen showing
 * something odd is better than a screen that throws.
 */
export function formatDayTime(value, timeZone, locale) {
    const instant = Date.parse(value);
    if (!Number.isFinite(instant))
        return value;
    return `${formatDisplayDate(instant, timeZone, locale)}, ${formatDisplayTime(instant, timeZone, locale)}`;
}
/** A plain calendar date, `YYYY-MM-DD`, with no time and no zone. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;
/**
 * One date, written the way the shop writes one, whether the wire carried a day or an instant.
 *
 * ---
 *
 * **Two kinds of value arrive at a screen and they must not be formatted the same way.**
 *
 * A `date` column (a prescription's issue date, a job's promised date, a loan's return date) is a
 * calendar date. It has no time and belongs to no zone. An instant (`timestamptz`: when a consent
 * was recorded, when a staff member was offboarded) is a point in time, and Appendix J says it
 * renders in the branch's zone, never the browser's.
 *
 * Putting a calendar date through a zone moves it. `Date.parse('2026-02-11')` is UTC midnight, so
 * in Karachi it reads 11 Feb and in New York it reads **10 Feb**:
 *
 * ```
 * Asia/Karachi      Feb 11, 2026
 * America/New_York  Feb 10, 2026
 * ```
 *
 * Pakistan is UTC+5 so every screen in this product renders correctly today, and §13's country
 * packs are what makes that luck rather than correctness. A prescription expiry shown a day early
 * is the kind of wrong a dispenser acts on.
 *
 * So the shape decides: a calendar date is formatted at UTC, which is the only zone that leaves it
 * alone, and an instant is formatted in the branch's. The branch timezone is still required for
 * both, because a caller that does not have one is a caller that cannot render the instant case and
 * should not be given a function that silently works for half its inputs.
 *
 * **An unparseable value comes back as itself.** A date the screen cannot read is still something a
 * shopkeeper may need to report, and an empty space is not.
 */
export function formatDay(value, timeZone, locale) {
    if (CALENDAR_DAY.test(value)) {
        // `T00:00:00Z` stated rather than relying on the bare-date parse rule, so the instant is the
        // one this function means rather than the one the runtime chose.
        return formatDisplayDate(Date.parse(`${value}T00:00:00.000Z`), 'UTC', locale);
    }
    const instant = Date.parse(value);
    if (!Number.isFinite(instant))
        return value;
    return formatDisplayDate(instant, timeZone, locale);
}
//# sourceMappingURL=format.js.map